import Phaser from 'phaser';
import { showBugReportOverlay } from './BugReportOverlay';
import { GAME_W } from '../utils/MobileHelper';
import { FONT_BODY } from '../utils/Typography';
import { captureCanvas } from '../systems/BugReportContext';

// === BOUTON BUG REPORT GLOBAL ===
// Petit bouton 🐛 discret en haut à droite, à appeler dans chaque scène.
// Au clic : ouvre le formulaire de rapport (DOM overlay) avec contexte run optionnel.

/**
 * Ajoute un bouton 🐛 en haut à droite de la scène.
 * @param scene Scène hôte
 * @param contextProvider Fonction optionnelle retournant une ligne de contexte
 *   (ex : "Étage 3, 45 or, 2 persos vivants"). Appelée au moment du clic.
 */
export function addBugReportButton(
  scene: Phaser.Scene,
  contextProvider?: () => string,
): Phaser.GameObjects.Text {
  const btn = scene.add.text(GAME_W - 6, 6, '🐛', {
    fontSize: '14px',
    fontFamily: FONT_BODY,
    backgroundColor: '#1a0a0a',
    padding: { x: 5, y: 3 },
  }).setOrigin(1, 0).setDepth(500).setAlpha(0.55);

  btn.setInteractive({ useHandCursor: true });
  btn.on('pointerover', () => btn.setAlpha(1));
  btn.on('pointerout', () => btn.setAlpha(0.55));
  btn.on('pointerdown', async () => {
    const sceneKey = scene.scene.key;
    const ctx = contextProvider?.();
    // Capturer le canvas AVANT d'ouvrir l'overlay (sinon l'overlay DOM apparaît
    // au-dessus mais le canvas Phaser n'est pas affecté — ceci-dit, on capture
    // en amont pour garantir que rien d'autre ne perturbe la frame).
    const canvas = scene.game.canvas;
    const screenshot = await captureCanvas(canvas).catch(() => null);
    showBugReportOverlay(sceneKey, undefined, ctx, screenshot);
  });

  return btn;
}

/**
 * Helper : extrait une ligne de contexte d'un RunManager (étage, or, équipe).
 * À utiliser comme contextProvider dans les scènes qui ont this.run.
 */
export function runContextLine(run: {
  getMap?: () => { currentNodeId: string | null; nodes: Array<{ id: string; floor: number; type: string }> };
  getGold?: () => number;
  getTeam?: () => Array<{ alive: boolean; class: string; name: string }>;
}): string {
  const parts: string[] = [];
  try {
    const map = run.getMap?.();
    if (map?.currentNodeId) {
      const cur = map.nodes.find(n => n.id === map.currentNodeId);
      if (cur) parts.push(`Étage ${cur.floor} (${cur.type})`);
    }
    const gold = run.getGold?.();
    if (gold !== undefined) parts.push(`${gold} or`);
    const team = run.getTeam?.();
    if (team && team.length > 0) {
      const alive = team.filter(t => t.alive);
      const classes = alive.map(t => t.class).join(',');
      parts.push(`${alive.length}/${team.length} vivants (${classes})`);
    }
  } catch {
    // run a un format inattendu — on retourne ce qu'on a déjà collecté
  }
  return parts.join(' · ') || 'pas de run actif';
}
